// useBillboard.js
import * as THREE from 'three';
import * as TWEEN from '@tweenjs/tween.js';
import { getPhotoUrl } from '@/constants/urls';

/**
 * Creates the billboard (with border) and returns a THREE.Group.
 * We'll attach a CanvasTexture to its material in the main component.
 * 
 * @param {THREE.Scene} scene - The Three.js scene to add the billboard to
 * @returns {THREE.Group} - The billboard group
 */
export function createBillboard(scene) {
  const width = 1;
  const height = 2;
  const borderThickness = 0.05;
  const sideBorderThickness = 0.05;

  // Main billboard geometry
  const billboardGeometry = new THREE.BoxGeometry(width, height, borderThickness);
  const billboardMaterial = new THREE.MeshBasicMaterial({ color: 0xeeeeee });
  const billboard = new THREE.Mesh(billboardGeometry, billboardMaterial);

  // Border geometry
  const borderGeometry = new THREE.BoxGeometry(width + 2 * sideBorderThickness, height + 2 * borderThickness, borderThickness);
  const borderMaterial = new THREE.MeshBasicMaterial({ color: 0x222222 });
  const border = new THREE.Mesh(borderGeometry, borderMaterial);
  border.position.setZ(-borderThickness / 2);

  // Group them
  const billboardGroup = new THREE.Group();
  billboardGroup.add(billboard);
  billboardGroup.add(border);

  // Position & rotation
  billboardGroup.position.set(1.1, 1.1, -0.5);
  billboardGroup.rotation.y = -Math.PI / 8;
  billboardGroup.scale.set(0, 0, 0);

  scene.add(billboardGroup);

  // Shadows
  billboardGroup.traverse((object) => {
    if (object.isMesh) {
      object.castShadow = true;
      object.receiveShadow = true;
      object.material.envMapIntensity = 0.3;
    }
  });

  return billboardGroup;
}

/**
 * Shows the billboard with a tween scaling from (0,0,0) to (1,1,1).
 * 
 * @param {THREE.Group} billboardGroup - The billboard group to show
 * @param {Object} billboardVisibleRef - Reference to visibility flag
 */
export function showBillboard(billboardGroup, billboardVisibleRef) {
  new TWEEN.Tween(billboardGroup.scale)
    .to({ x: 1, y: 1, z: 1 }, 1000)
    .easing(TWEEN.Easing.Exponential.Out)
    .start();
  billboardVisibleRef.value = true;
}

/**
 * Hides the billboard with a tween scaling down to 0.
 * 
 * @param {THREE.Group} billboardGroup - The billboard group to hide
 * @param {Object} billboardVisibleRef - Reference to visibility flag
 */
export function hideBillboard(billboardGroup, billboardVisibleRef) {
  new TWEEN.Tween(billboardGroup.scale)
    .to({ x: 0, y: 0, z: 0 }, 1000)
    .easing(TWEEN.Easing.Exponential.In)
    .start();
  billboardVisibleRef.value = false;
}

/**
 * Loads a texture from URL (wrapped in a Promise).
 * 
 * @param {string} url - The URL of the texture to load
 * @returns {Promise<THREE.Texture>} - The loaded texture
 */
function loadTexture(url) {
  return new Promise((resolve, reject) => {
    new THREE.TextureLoader().load(url, resolve, undefined, reject);
  });
}

/**
 * Splits text into lines by max length.
 * 
 * @param {string} text - The text to split
 * @param {number} maxLineLength - Maximum characters per line
 * @returns {Array<string>} - Array of text lines
 */
function splitTextIntoLines(text, maxLineLength) {
  const words = text.split(' ');
  const lines = [];
  let currentLine = words[0];

  for (let i = 1; i < words.length; i++) {
    if (currentLine.length + words[i].length + 1 <= maxLineLength) {
      currentLine += ' ' + words[i];
    } else {
      lines.push(currentLine);
      currentLine = words[i];
    }
  }
  lines.push(currentLine);
  return lines;
}

/**
 * Preserves aspect ratio for images.
 * 
 * @param {number} srcWidth - Source width
 * @param {number} srcHeight - Source height
 * @param {number} maxWidth - Maximum width constraint
 * @param {number} maxHeight - Maximum height constraint
 * @returns {Object} - Object with width and height properties
 */
function calculateAspectRatioFit(srcWidth, srcHeight, maxWidth, maxHeight) {
  const ratio = Math.min(maxWidth / srcWidth, maxHeight / srcHeight);
  return { width: srcWidth * ratio, height: srcHeight * ratio };
}

/**
 * Updates the billboard's CanvasTexture with product info:
 * draws the product image + text.
 * 
 * @param {Object} options - Options object
 * @param {Object} options.product - The product to display
 * @param {THREE.Mesh} options.billboardMesh - The billboard mesh
 * @param {HTMLCanvasElement} options.billboardCanvas - The canvas element
 * @param {CanvasRenderingContext2D} options.billboardContext - The canvas context
 * @returns {Promise<void>}
 */
export async function updateBillboardTexture({
  product,
  billboardMesh,
  billboardCanvas,
  billboardContext
}) {
  // Clear
  billboardContext.clearRect(0, 0, billboardCanvas.width, billboardCanvas.height);
  billboardContext.fillStyle = '#FFFFFF';
  billboardContext.fillRect(0, 0, billboardCanvas.width, billboardCanvas.height);

  // Pick image
  let imgUrl = '';
  if (product.images && product.images.length > 1) {
    imgUrl = getPhotoUrl(product.images[1]);
  } else if (product.images && product.images.length > 0) {
    imgUrl = getPhotoUrl(product.images[0]);
  }

  // Load texture
  try {
    if (imgUrl) {
      const texture = await loadTexture(imgUrl);
      const imgWidth = texture.image.width;
      const imgHeight = texture.image.height;

      // Fit
      const { width, height } = calculateAspectRatioFit(
        imgWidth,
        imgHeight,
        billboardCanvas.width - 20,
        billboardCanvas.height - 240
      );
      const x = (billboardCanvas.width - width) / 2;
      const y = (billboardCanvas.height - 240 - height) / 2 + 10;
      billboardContext.drawImage(texture.image, x, y, width, height);
    }
  } catch (e) {
    console.error('Error loading billboard texture:', e);
  }

  // Product name
  billboardContext.fillStyle = '#000000';
  billboardContext.font = '13px Poppins';
  let yPos = billboardCanvas.height - 210;
  const nameChunks = splitTextIntoLines(product.name || '', 36);
  nameChunks.forEach(chunk => {
    billboardContext.fillText(chunk, 10, yPos);
    yPos += 15;
  });

  // Product description
  billboardContext.font = 'ExtraLight 8px Poppins';
  billboardContext.fillStyle = '#333333';
  const descriptionChunks = splitTextIntoLines(product.description || '', 36);
  descriptionChunks.forEach(chunk => {
    billboardContext.fillText(chunk, 10, yPos);
    yPos += 15;
  });

  // Force update
  billboardMesh.material.map.needsUpdate = true;
}